import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportCard from './components/ReportCard';
import MetricWidget from './components/MetricWidget';
import InteractiveChart from './components/InteractiveChart';
import ReportFilters from './components/ReportFilters';
import CustomReportBuilder from './components/CustomReportBuilder';
import { dashboardService } from '../../services/api';

const ReportsAndAnalytics = () => {
  const [currentLanguage, setCurrentLanguage] = useState('pt');
  const [reports, setReports] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [chartsData, setChartsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'pt';
    setCurrentLanguage(savedLanguage);
    
    // Buscar hotel selecionado do localStorage
    const storedHotel = localStorage.getItem('selectedHotel');
    if (storedHotel) {
      try {
        setSelectedHotel(JSON.parse(storedHotel));
      } catch (error) {
        console.error('Erro ao parsear hotel selecionado:', error);
      }
    }
  }, []);

  // Carregar dados de relatórios e métricas da API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Carregar métricas do dashboard
        const dashboardStats = await dashboardService.getStats();
        
        // Transformar dados do dashboard em métricas
        const metrics = [
          {
            id: 'occupancy',
            title: 'Taxa de Ocupação',
            value: dashboardStats.occupancyRate || 0,
            type: 'percentage',
            trend: dashboardStats.occupancyTrend || 0,
            icon: 'Bed',
            iconColor: 'var(--color-primary)',
            iconBg: 'bg-primary/10',
            chart: true
          },
          {
            id: 'adr',
            title: 'Diária Média (ADR)',
            value: dashboardStats.averageDailyRate || 0,
            type: 'currency',
            trend: dashboardStats.adrTrend || 0,
            icon: 'DollarSign',
            iconColor: 'var(--color-success)',
            iconBg: 'bg-success/10',
            chart: true
          },
          {
            id: 'revpar',
            title: 'RevPAR',
            value: dashboardStats.revpar || 0,
            type: 'currency',
            trend: dashboardStats.revparTrend || 0,
            icon: 'TrendingUp',
            iconColor: 'var(--color-warning)',
            iconBg: 'bg-warning/10',
            chart: true
          },
          {
            id: 'satisfaction',
            title: 'Satisfação Média',
            value: dashboardStats.satisfactionScore || 0,
            type: 'number',
            trend: dashboardStats.satisfactionTrend || 0,
            icon: 'Star',
            iconColor: 'var(--color-accent)',
            iconBg: 'bg-accent/10',
            chart: false
          }
        ];
        
        setMetricsData(metrics);
        
        // TODO: Implementar endpoints específicos para relatórios e gráficos
        // Por enquanto, usar dados estáticos com comentários indicando que devem ser substituídos
        setReports([
          {
            id: 1,
            title: "Relatório de Ocupação",
            description: "Análise detalhada das taxas de ocupação por período",
            icon: "BarChart3",
            status: "ready",
            lastUpdated: "26/07/2025 16:30",
            frequency: "Diário",
            format: "PDF"
          },
          {
            id: 2,
            title: "Análise Financeira",
            description: "Receitas, despesas e margem de lucro",
            icon: "DollarSign",
            status: "generating",
            lastUpdated: "26/07/2025 15:45",
            frequency: "Semanal",
            format: "Excel"
          },
          {
            id: 3,
            title: "Satisfação do Hóspede",
            description: "Métricas de satisfação e feedback dos clientes",
            icon: "Heart",
            status: "scheduled",
            lastUpdated: "25/07/2025 18:20",
            frequency: "Mensal",
            format: "PDF"
          },
          {
            id: 4,
            title: "Performance Operacional",
            description: "Indicadores de eficiência operacional",
            icon: "Activity",
            status: "ready",
            lastUpdated: "26/07/2025 14:15",
            frequency: "Diário",
            format: "CSV"
          }
        ]);
        
        setChartsData([
          {
            id: 'revenue-trend',
            title: 'Tendência de Receita',
            description: 'Receita mensal nos últimos 12 meses',
            defaultType: 'line',
            data: [
              { name: 'Jan', value: 45000 },
              { name: 'Fev', value: 52000 },
              { name: 'Mar', value: 48000 },
              { name: 'Abr', value: 61000 },
              { name: 'Mai', value: 55000 },
              { name: 'Jun', value: 67000 },
              { name: 'Jul', value: 73000 },
              { name: 'Ago', value: 69000 },
              { name: 'Set', value: 58000 },
              { name: 'Out', value: 62000 },
              { name: 'Nov', value: 71000 },
              { name: 'Dez', value: 78000 }
            ],
            insights: [
              "Crescimento de 15% na receita comparado ao ano anterior",
              "Pico de receita observado em dezembro devido às festividades",
              "Oportunidade de melhoria nos meses de março e setembro"
            ]
          },
          {
            id: 'booking-sources',
            title: 'Fontes de Reserva',
            description: 'Distribuição das reservas por canal',
            defaultType: 'pie',
            data: [
              { name: 'Site Direto', value: 35 },
              { name: 'Booking.com', value: 28 },
              { name: 'Expedia', value: 18 },
              { name: 'Agências', value: 12 },
              { name: 'Outros', value: 7 }
            ],
            insights: [
              "Site direto representa 35% das reservas, indicando forte marca",
              "OTAs ainda dominam 46% das reservas",
              "Oportunidade de reduzir dependência de terceiros"
            ]
          }
        ]);
        
      } catch (error) {
        console.error('Erro ao carregar dados de relatórios:', error);
        // Em caso de erro, usar dados vazios
        setReports([]);
        setMetricsData([]);
        setChartsData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleGenerateReport = (reportId) => {
    console.log('Gerando relatório:', reportId);
    // Implement report generation logic
  };

  const handleScheduleReport = (reportId) => {
    console.log('Agendando relatório:', reportId);
    // Implement report scheduling logic
  };

  const handleViewReport = (reportId) => {
    console.log('Visualizando relatório:', reportId);
    // Implement report viewing logic
  };

  const handleApplyFilters = (filters) => {
    console.log('Aplicando filtros:', filters);
    setFilters(filters);
    // Implement filter application logic
  };

  const handleResetFilters = () => {
    console.log('Resetando filtros');
    setFilters({});
    // Implement filter reset logic
  };

  const handleSaveCustomReport = (config) => {
    console.log('Salvando relatório personalizado:', config);
    setShowCustomBuilder(false);
    // Implement custom report saving logic
  };

  const handleDrillDown = (data) => {
    console.log('Drill down nos dados:', data);
    // Implement drill-down functionality
  };

  const handleWidgetDragStart = (e, widgetId) => {
    setDraggedWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleWidgetDragEnd = () => {
    setDraggedWidget(null);
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Relatórios e Análises', path: '/reports-and-analytics', icon: 'BarChart3', isLast: true }
  ];

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: 'LayoutDashboard' },
    { id: 'reports', label: 'Relatórios', icon: 'FileText' },
    { id: 'analytics', label: 'Análises', icon: 'BarChart3' },
    { id: 'insights', label: 'Insights', icon: 'Lightbulb' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb customItems={breadcrumbItems} />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">Relatórios e Análises</h1>
                  {selectedHotel && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {selectedHotel.name}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm max-w-2xl">
                  Insights abrangentes e métricas de performance para tomada de decisões estratégicas
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => console.log('Exportar dados')}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-card text-foreground font-medium rounded-lg border border-border hover:bg-accent transition-all duration-200 hover:shadow-sm"
                >
                  <Icon name="Download" size={18} />
                  <span className="hidden sm:inline">Exportar Dados</span>
                </button>
                
                <button
                  onClick={() => setShowCustomBuilder(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                >
                  <Icon name="Plus" size={18} />
                  <span>Relatório Personalizado</span>
                </button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2 bg-muted/30 p-1 rounded-lg">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Report Filters */}
            <div className="mb-8">
              <ReportFilters
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Carregando dados de relatórios...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <>
                    {/* Key Metrics Dashboard */}
                    <div className="mb-8">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <h2 className="text-xl font-bold text-foreground">Métricas Principais</h2>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="Info" size={16} />
                          <span className="hidden sm:inline">Arraste os widgets para reorganizar</span>
                        </div>
                      </div>
                  
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {metricsData.map((metric) => (
                          <MetricWidget
                            key={metric.id}
                            metric={metric}
                            onDragStart={handleWidgetDragStart}
                            onDragEnd={handleWidgetDragEnd}
                            isDragging={draggedWidget === metric.id}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Interactive Charts */}
                    <div className="mb-8">
                      <h2 className="text-xl font-bold text-foreground mb-6">
                        Análises Interativas
                      </h2>
                      
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {chartsData.map((chart) => (
                          <InteractiveChart
                            key={chart.id}
                            chart={chart}
                            onDrillDown={handleDrillDown}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Reports Tab */}
                {activeTab === 'reports' && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-foreground mb-6">
                      Relatórios Pré-configurados
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {reports.map((report) => (
                        <ReportCard
                          key={report.id}
                          report={report}
                          onGenerate={handleGenerateReport}
                          onSchedule={handleScheduleReport}
                          onView={handleViewReport}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-foreground mb-6">
                      Análises Avançadas
                    </h2>
                    
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                      {chartsData.map((chart) => (
                        <InteractiveChart
                          key={chart.id}
                          chart={chart}
                          onDrillDown={handleDrillDown}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Insights Tab */}
                {activeTab === 'insights' && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-foreground mb-6">
                      Insights Inteligentes
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {chartsData.map((chart) => (
                        <div key={chart.id} className="bg-card border border-border rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Icon name="Lightbulb" size={20} className="text-primary" />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">{chart.title}</h3>
                          </div>
                          
                          <div className="space-y-3">
                            {chart.insights?.map((insight, index) => (
                              <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-foreground">{insight}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Report Builder Modal */}
                {showCustomBuilder && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-background rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                      <CustomReportBuilder
                        onSave={handleSaveCustomReport}
                        onCancel={() => setShowCustomBuilder(false)}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportsAndAnalytics;